import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, MessageCircle, Send } from "lucide-react";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { TikTokMarketplaceCreator } from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import { ageLabel, creatorPick, formatMoney } from "./creatorSearchFormat";
import { GmvCell, SignalChips } from "./CreatorResultsTable";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm tabular-nums text-foreground">{children}</dd>
    </div>
  );
}

/** Everything TikTok returned for one creator, opened from a result row. */
export function CreatorDetailSheet({
  creator,
  onOpenChange,
}: {
  creator: TikTokMarketplaceCreator | null;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  return (
    <Sheet open={creator !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        {creator && (
          <div className="space-y-6">
            <SheetHeader className="space-y-3 text-left">
              <div className="flex items-center gap-3">
                <UserAvatar src={creator.avatarUrl} name={creator.nickname || creator.username} className="h-14 w-14 shrink-0 text-base" />
                <div className="min-w-0">
                  <SheetTitle className="truncate">{creator.nickname || `@${creator.username}`}</SheetTitle>
                  <SheetDescription className="truncate">
                    @{creator.username}
                    {creator.region ? ` · ${creator.region}` : ""}
                  </SheetDescription>
                </div>
              </div>
              <SignalChips creator={creator} hideEmpty />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => navigate("/brand/tiktok?tab=invites", { state: { inviteCreators: [creatorPick(creator)] } })}
                >
                  <Send className="h-4 w-4" />
                  Collab invite
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/brand/tiktok?tab=messages", { state: { messageCreator: creatorPick(creator) } })}
                >
                  <MessageCircle className="h-4 w-4" />
                  Message
                </Button>
                <Button asChild variant="ghost" size="sm">
                  <a href={creator.profileUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    Open on TikTok
                  </a>
                </Button>
              </div>
            </SheetHeader>

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sales, last 30 days</h3>
              <dl className="divide-y divide-border">
                <Row label="GMV">
                  <GmvCell creator={creator} />
                </Row>
                {creator.gmv && (
                  <>
                    <Row label="From videos">{formatMoney(creator.videoGmv)}</Row>
                    <Row label="From LIVE">{formatMoney(creator.liveGmv)}</Row>
                  </>
                )}
                {creator.unitsSold && <Row label="Units sold, lifetime">{creator.unitsSold.label}</Row>}
              </dl>
              {!creator.gmv && creator.gmvRange && (
                <p className="mt-1 text-xs text-muted-foreground">This creator shares a sales range, not exact figures.</p>
              )}
            </section>

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reach</h3>
              <dl className="divide-y divide-border">
                <Row label="Followers">{creator.followers === null ? "—" : formatCompactCount(creator.followers)}</Row>
                <Row label="Avg shoppable video views">
                  {creator.avgVideoViews === null ? "—" : formatCompactCount(creator.avgVideoViews)}
                </Row>
                <Row label="Avg LIVE viewers">
                  {creator.avgLiveViewers ? formatCompactCount(creator.avgLiveViewers) : "No LIVE"}
                </Row>
              </dl>
            </section>

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Audience</h3>
              <dl className="divide-y divide-border">
                <Row label="Top ages">
                  {creator.audience.ageRanges.length ? creator.audience.ageRanges.map(ageLabel).join(", ") : "—"}
                </Row>
                <Row label="Majority">
                  {creator.audience.gender && creator.audience.genderPercent
                    ? `${creator.audience.genderPercent}% ${creator.audience.gender === "FEMALE" ? "women" : "men"}`
                    : "—"}
                </Row>
              </dl>
            </section>

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">On TikTok Shop</h3>
              <dl className="divide-y divide-border">
                <Row label="Categories">
                  {creator.categories.some((c) => c.name)
                    ? creator.categories.filter((c) => c.name).map((c) => c.name).join(", ")
                    : "—"}
                </Row>
                <Row label="Creator level">{creator.signals.level === null ? "—" : `Lv. ${creator.signals.level}`}</Row>
                <Row label="Promotion score">
                  {creator.signals.ppsScore === null ? "—" : `${creator.signals.ppsScore.toFixed(1)} / 5`}
                </Row>
                <Row label="Management">{creator.signals.agencyName ?? "Independent"}</Row>
              </dl>
            </section>

            {creator.affiliate && (
              <p className="rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800 dark:bg-brand-950 dark:text-brand-200">
                {creator.affiliate.name} is already an affiliate in your program with this TikTok handle.
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
